import { Box, Card, CardContent, Typography, Tooltip, Avatar } from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import OptionsMenu from '../general/OptionsMenu';
import WorkflowStateBadge from './WorkflowStateBadge';
import { ContractState, isStateAbove } from '../../enums/ContractState';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.mjs`;

const ContractItem = ({ contract, handleReset, handleDelete }) => {
    const [pdf, setPDF] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const containerRef = useRef(null);
    const cached = contract?.cached || {};
    const displayContractState = contract?.cached?.voidCount === 0 && isStateAbove(contract.state, ContractState.FINISHED_REVIEW)
        ? ContractState.HEALTHY
        : contract.state;

    const cachedMetrics = [
        {
            label: 'Klauseln',
            value: cached.clauseCount ?? 0,
            icon: <ArticleOutlinedIcon fontSize="inherit" />,
            color: 'text.secondary',
            backgroundColor: '#f5f5f5'
        },
        {
            label: 'Gültig',
            value: cached.validCount ?? 0,
            icon: <CheckCircleOutlineIcon fontSize="inherit" />,
            color: '#2e7d32',
            backgroundColor: '#e8f5e9'
        },
        {
            label: 'Unfair',
            value: cached.unfairCount ?? 0,
            icon: <ReportProblemOutlinedIcon fontSize="inherit" />,
            color: '#ef6c00',
            backgroundColor: '#fff3e0'
        },
        {
            label: 'Unwirksam',
            value: cached.voidCount ?? 0,
            icon: <BlockOutlinedIcon fontSize="inherit" />,
            color: '#c62828',
            backgroundColor: '#ffebee'
        },
        {
            label: 'Korrigiert',
            value: cached.errorCount ?? 0,
            icon: <EditNoteOutlinedIcon fontSize="inherit" />,
            color: '#6a1b9a',
            backgroundColor: '#f3e5f5',
            tooltip: 'Korrigiert: Klauseln, bei denen Anwälte die KI-Bewertung korrigiert haben'
        }
    ];

    useEffect(() => {
        const loadPdf = async () => {
            setLoadError(false);
            setPDF(null);

            try {
                const options = {
                    url: process.env.REACT_APP_API_BASE_URL + '/contracts/files/' + contract.file,
                    withCredentials: true,
                    httpHeaders: {
                        mode: 'cors',
                        authorization: 'Bearer ' + localStorage.getItem("jwtToken"),
                    }
                };
                const loadingTask = pdfjsLib.getDocument(options);
                const loadedPdf = await loadingTask.promise;
                setPDF(loadedPdf);
            } catch (error) {
                setLoadError(true);
            }
        };

        if (contract?.file) {
            loadPdf();
        } else {
            setLoadError(true);
        }
    }, [contract]);

    useEffect(() => {
        const renderPage = async () => {
            if (!pdf || !containerRef.current) return;

            try {
                const page = await pdf.getPage(1);
                if (!containerRef.current) return;

                while (containerRef.current.firstChild) {
                    containerRef.current.removeChild(containerRef.current.firstChild);
                }

                const canvas = document.createElement('canvas');
                containerRef.current.appendChild(canvas);

                const context = canvas.getContext('2d');
                const viewport = page.getViewport({ scale: containerRef.current.clientWidth / page.getViewport({ scale: 1 }).width });

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: context, viewport }).promise;
            } catch (renderError) {
                console.error("Error rendering page:", renderError);
                setLoadError(true);
            }
        };

        renderPage();
    }, [pdf]);

    return (
        <Card
            sx={{
                cursor: 'pointer',
                boxShadow: 'none',
                height: '100%',
                maxHeight: '390px',
                position: 'relative', // Wichtig für absolute Positionierung des Icons
                '&:hover': {
                    bgcolor: '#ffffff99'
                }
            }}
        >
            {/* ABSOLUTE POSITIONIERTES ICON (OBEN LINKS) */}
            {contract.correctionRequested && (
                <Tooltip title="Angebot für Anwaltskorrektur angefordert">
                    <Avatar sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        zIndex: 100, // Über dem PDF/Content liegen
                        width: 32,
                        height: 32,
                        bgcolor: '#ede7f6', // Helles Lila (Deep Purple 50)
                        border: '1px solid #d1c4e9',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <RequestQuoteIcon sx={{ fontSize: 18, color: '#5e35b1' }} />
                    </Avatar>
                </Tooltip>
            )}

            <CardContent sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                maxHeight: '-webkit-fill-available'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignContent: 'start',
                    gap: 2,
                    mb: 2
                }}>
                    {/* PDF Preview Area */}
                    {loadError ? (
                        <Box
                            width={'40%'}
                            sx={{
                                bgcolor: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                minHeight: '150px',
                                border: '1px dashed #ccc'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 1 }}>
                                Vorschau nicht verfügbar
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            width={'40%'}
                            height={'auto'}
                            sx={{ position: 'relative', overflow: 'hidden' }}
                            ref={containerRef}
                        ></Box>
                    )}

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '66%'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2
                        }}>
                            <Typography sx={{ fontWeight: 'bold', width: '100%' }}>
                                {contract?.createdAt ? new Date(contract.createdAt).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: '2-digit'
                                }) : '-'}
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'start',
                                alignItems: 'center',
                            }}>
                                <OptionsMenu contract={contract} handleReset={handleReset} handleDelete={handleDelete} />
                            </Box>
                        </Box>
                        <Typography variant="h6" mr={1} sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: 'bold'
                        }}>
                            {contract?.title || 'Ohne Titel'}
                        </Typography>
                        <Typography mb={1} color="text.secondary" sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {contract?.description}
                        </Typography>
                        <Typography mt={0.5} color="text.secondary">
                            {contract?.email
                                ? `${contract.email} ${contract.emailVerified ? '(verifiziert) ' : '(nicht verifiziert)'}`
                                : 'Kein Nutzer bekannt.'}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                                gap: 0.75,
                                mt: 1.5
                            }}
                        >
                            {cachedMetrics.map((metric) => {
                                const isEmpty = metric.value === 0;
                                const content = (
                                    <Box
                                        key={metric.label}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 0,
                                            minHeight: 34,
                                            borderRadius: 1,
                                            bgcolor: isEmpty ? '#eeeeee' : metric.backgroundColor,
                                            color: isEmpty ? '#9e9e9e' : metric.color,
                                            px: 0.5,
                                            gap: 0.25,
                                            opacity: isEmpty ? 0.75 : 1
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.25,
                                            fontSize: 17,
                                            lineHeight: 1
                                        }}>
                                            {metric.icon}
                                            <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                                                {metric.value}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );

                                return (
                                    <Tooltip key={metric.label} title={metric.tooltip || metric.label}>
                                        {content}
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>

                {/* Workflow Badge (Unverändert, volle Breite im Kontext) */}
                <WorkflowStateBadge contractState={displayContractState} />
            </CardContent>
        </Card>
    );
};

export default ContractItem;
